/**
 * HeyDex inside the HeyDay News app, which holds HeyDex in its HeyDex tab
 * (`@heyday/news-api/heydex` is the bridge). The app names itself in the web
 * view's user agent, so this is known before anything renders, on every load.
 * Inside the app the tab bar is the way out of HeyDex and the app's own You and
 * Settings hold the account and the appearance (a color scheme and a theme for
 * each mode), so HeyDex shows neither, and the reader's box is the app's
 * on iOS (`callAppBox`). Android keeps its WASM box and bridges its network
 * separately to the device; everything else is the same HeyDex.
 */
import {
  type Appearance,
  HEYDEX_APPEARANCE_GLOBAL,
  HEYDEX_COMMAND_EVENT,
  HEYDEX_THEME_GLOBAL,
  type HeyDexBoxArgs,
  type HeyDexBoxCall,
  type HeyDexBoxMethod,
  type HeyDexCommand,
  type HeyDexEvent,
  isAppUserAgent,
  parseAppearance,
  parseHeyDexCommand,
} from "@heyday/news-api/heydex";
import { type AppThemeId, parseThemeId } from "@heyday/news-api/themes";
import { BoxError, boxErrorCode } from "@heydex/shared/box";

export const insideHeyDayNewsApp: boolean =
  typeof navigator !== "undefined" && isAppUserAgent(navigator.userAgent);

/**
 * The iOS and macOS apps hold the reader's box (TrumanKit) and HeyDex reaches it through
 * `callAppBox`; Android's web view runs its WASM box in the page and bridges
 * HostTransport to the device separately. Box ownership does not choose networking.
 */
export const boxInApp: boolean = insideHeyDayNewsApp && !/\bAndroid\b/.test(navigator.userAgent);

/** HeyDex framed by heyday.news's HeyDex page, under the site's masthead, which carries HeyDex's name. */
export const insideHeyDayNewsSite: boolean = typeof window !== "undefined" && window.top !== window.self;

// index.css reads this mark: on a phone the site's masthead holds HeyDex's controls.
if (insideHeyDayNewsSite) document.documentElement.dataset.heydaySite = "";

// The app lays its web view inside the device's top and bottom safe area; index.css
// reads this mark to ignore the insets Android's WebView reports there anyway.
if (insideHeyDayNewsApp) document.documentElement.dataset.heydayApp = "";

/** The appearance the app chose, while HeyDex runs inside it; null elsewhere or before the app has said. */
export function appAppearance(): Appearance | null {
  if (!insideHeyDayNewsApp) return null;
  return parseAppearance(window[HEYDEX_APPEARANCE_GLOBAL]);
}

/** The theme the app chose for its current appearance, while HeyDex runs inside it; null elsewhere. */
export function appTheme(): AppThemeId | null {
  if (!insideHeyDayNewsApp) return null;
  return parseThemeId(window[HEYDEX_THEME_GLOBAL]);
}

/** Calls `listener` with each command the app sends; returns the unsubscribe. */
export function onAppCommand(listener: (command: HeyDexCommand) => void): () => void {
  if (!insideHeyDayNewsApp) return () => {};
  const receive = (event: Event) => {
    const command = event instanceof CustomEvent ? parseHeyDexCommand(event.detail) : null;
    if (command) listener(command);
  };
  window.addEventListener(HEYDEX_COMMAND_EVENT, receive);
  return () => window.removeEventListener(HEYDEX_COMMAND_EVENT, receive);
}

/** Tells the app something; outside the app there is no one to tell. */
export function postToApp(event: HeyDexEvent): void {
  const bridge = window.ReactNativeWebView;
  if (insideHeyDayNewsApp && bridge) bridge.postMessage(JSON.stringify(event));
}

const boxCalls = new Map<number, { resolve: (value: unknown) => void; reject: (error: BoxError) => void }>();
let nextBoxCall = 0;
/** The page answers the app's box replies for as long as it lives, from its first box call. */
let receivingBoxReplies = false;

/**
 * One call on the app's box (TrumanKit), settled by the app's `boxReply` with the
 * same id. The value is the reply's JSON as the app sent it; a failure is a
 * `BoxError` with the app's code.
 */
export function callAppBox<M extends HeyDexBoxMethod>(method: M, args: HeyDexBoxArgs[M]): Promise<unknown> {
  if (!boxInApp || !window.ReactNativeWebView) {
    return Promise.reject(new BoxError("unavailable", "the app's box is reachable only inside a HeyDay News native box"));
  }
  if (!receivingBoxReplies) {
    receivingBoxReplies = true;
    onAppCommand((command) => {
      if (command.type !== "boxReply") return;
      const call = boxCalls.get(command.id);
      if (!call) throw new Error(`box: the app replied to unknown call ${command.id}`);
      boxCalls.delete(command.id);
      if (command.ok) call.resolve(command.value);
      else call.reject(new BoxError(boxErrorCode(command.code), command.message));
    });
  }
  return new Promise((resolve, reject) => {
    const id = nextBoxCall++;
    boxCalls.set(id, { resolve, reject });
    postToApp({ type: "box", id, method, args } as HeyDexBoxCall);
  });
}

/** A CSS colour as `#rrggbb`, whatever space the theme wrote it in, laid over `under` when it is translucent. */
function opaqueHex(color: string, under: string): string | null {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return null;
  context.fillStyle = under;
  context.fillRect(0, 0, 1, 1);
  context.fillStyle = color;
  context.fillRect(0, 0, 1, 1);
  const [red = 0, green = 0, blue = 0] = context.getImageData(0, 0, 1, 1).data;
  return `#${[red, green, blue].map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}

/** A theme token as the page computes it. */
function token(name: string): string {
  const probe = document.createElement("span");
  probe.style.color = `var(${name})`;
  probe.style.display = "none";
  document.body.append(probe);
  const color = getComputedStyle(probe).color;
  probe.remove();
  return color;
}

/**
 * Tells the app HeyDex's surface once its theme has resolved: `background` is the
 * page behind the conversation, so the app's bars around the view continue it.
 */
export function reportSurfaceToApp(background: string): void {
  if (!insideHeyDayNewsApp || !document.body) return;
  const page = opaqueHex(background, "#ffffff");
  if (!page) return;
  const colours = ["--foreground", "--muted-foreground", "--border", "--primary"].map((name) =>
    opaqueHex(token(name), page),
  );
  const [foreground, muted, border, accent] = colours;
  if (!foreground || !muted || !border || !accent) return;
  const scheme = document.documentElement.classList.contains("dark") ? "dark" : "light";
  postToApp({
    type: "surface",
    surface: { scheme, background: page, foreground, muted, border, accent },
  });
}

/** Whatever floats over the page and closes on Escape: a menu, a dialog, a popover or the phone sidebar. */
const OPEN_LAYER =
  '[data-open]:is([role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [data-slot$="popup"], [data-slot="sidebar"])';

/** Longer than any layer's closing animation. */
const FOCUS_RETURN_MS = 1000;

/**
 * The system back inside HeyDex: the topmost open layer closes, else HeyDex goes back
 * one view. False when there was nothing to close and nowhere to go back to, so the
 * app leaves the tab.
 */
export function goBackInHeyDex(history: { canGoBack: () => boolean; back: () => void }): boolean {
  if (document.querySelector(OPEN_LAYER)) {
    const target = document.activeElement ?? document.body;
    target.dispatchEvent(
      new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }),
    );
    // Closing, the layer hands focus back to what opened it once its animation ends, and
    // as a key closed it that control would show its focus ring and tooltip; the reader
    // pressed the phone's back, not a key, so the returned focus is let go.
    const release = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement) event.target.blur();
    };
    document.addEventListener("focusin", release);
    setTimeout(() => document.removeEventListener("focusin", release), FOCUS_RETURN_MS);
    return true;
  }
  if (history.canGoBack()) {
    history.back();
    return true;
  }
  return false;
}
